import React, { useState, useRef, useEffect } from 'react';
import { HelpCircle } from 'lucide-react';
import { useLocation } from 'react-router-dom';

interface TooltipProps {
  text: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
  children?: React.ReactNode;
}

export function Tooltip({ text, position = 'bottom', children }: TooltipProps) {
  const [show, setShow] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isClicked, setIsClicked] = useState(false);
  const [adjustedPosition, setAdjustedPosition] = useState(position);
  const location = useLocation();

  // Force right position for specific pages
  useEffect(() => {
    const forcedRightPages = ['/add-customer', '/add-transaction', '/top-up'];
    if (forcedRightPages.includes(location.pathname)) {
      setAdjustedPosition('right');
    } else {
      setAdjustedPosition('bottom'); // Force bottom position for other pages
    }
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShow(false);
        setIsClicked(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (show && tooltipRef.current && containerRef.current) {
      const updatePosition = () => {
        const tooltipRect = tooltipRef.current?.getBoundingClientRect();
        const containerRect = containerRef.current?.getBoundingClientRect();
        
        if (!tooltipRect || !containerRect) return;

        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const padding = 8;

        let newPosition = adjustedPosition;
        const tooltip = tooltipRef.current;

        // Reset any previous styles
        tooltip.style.left = '';
        tooltip.style.top = '';
        tooltip.style.right = '';
        tooltip.style.bottom = '';
        tooltip.style.transform = '';

        // Calculate available space in each direction
        const spaceBelow = viewportHeight - containerRect.bottom;
        const spaceRight = viewportWidth - containerRect.right;

        // For pages that need right-aligned tooltips
        if (location.pathname.match(/\/(add-customer|add-transaction|top-up)/)) {
          if (spaceRight < tooltipRect.width + padding) {
            newPosition = 'left';
          } else {
            newPosition = 'right';
          }
        } else {
          // For all other pages, center below
          newPosition = 'bottom';
          
          // If not enough space below, move to top
          if (spaceBelow < tooltipRect.height + padding) {
            newPosition = 'top';
          }
        }

        // Apply position
        switch (newPosition) {
          case 'top':
          case 'bottom': {
            let left = containerRect.left + (containerRect.width / 2);
            let top = newPosition === 'top' 
              ? containerRect.top - tooltipRect.height - padding
              : containerRect.bottom + padding;

            // Adjust horizontal position if overflowing
            if (left - (tooltipRect.width / 2) < padding) {
              left = tooltipRect.width / 2 + padding;
            } else if (left + (tooltipRect.width / 2) > viewportWidth - padding) {
              left = viewportWidth - (tooltipRect.width / 2) - padding;
            }

            tooltip.style.left = `${left}px`;
            tooltip.style.top = `${top}px`;
            tooltip.style.transform = 'translateX(-50%)';
            break;
          }
          case 'left':
          case 'right': {
            let left = newPosition === 'left'
              ? containerRect.left - tooltipRect.width - padding
              : containerRect.right + padding;
            let top = containerRect.top + (containerRect.height / 2);

            // Adjust vertical position if overflowing
            if (top - (tooltipRect.height / 2) < padding) {
              top = tooltipRect.height / 2 + padding;
            } else if (top + (tooltipRect.height / 2) > viewportHeight - padding) {
              top = viewportHeight - (tooltipRect.height / 2) - padding;
            }

            tooltip.style.left = `${left}px`;
            tooltip.style.top = `${top}px`;
            tooltip.style.transform = 'translateY(-50%)';
            break;
          }
        }

        setAdjustedPosition(newPosition);
      };

      updatePosition();
      window.addEventListener('resize', updatePosition);
      return () => window.removeEventListener('resize', updatePosition);
    }
  }, [show, adjustedPosition, location.pathname]);

  const handleInteraction = (type: 'enter' | 'leave' | 'click') => {
    if (type === 'click') {
      setIsClicked(!isClicked);
      setShow(!show);
    } else if (type === 'enter' && !isClicked) {
      setShow(true);
    } else if (type === 'leave' && !isClicked) {
      setShow(false);
    }
  };

  return (
    <div 
      className="relative inline-flex items-center"
      ref={containerRef}
    >
      {children || (
        <button
          type="button"
          className="text-gray-400 hover:text-gray-600 transition-colors"
          onMouseEnter={() => handleInteraction('enter')}
          onMouseLeave={() => handleInteraction('leave')}
          onClick={() => handleInteraction('click')}
        >
          <HelpCircle size={16} />
        </button>
      )}
      
      {show && (
        <div 
          ref={tooltipRef}
          className="fixed z-[9999] bg-gray-800 text-white text-xs sm:text-sm rounded-lg py-2 px-3 w-auto max-w-[calc(100vw-2rem)] sm:max-w-[20rem] animate-fade-in"
          role="tooltip"
        >
          <div className="relative">
            {text}
            <div 
              className={`absolute w-2 h-2 bg-gray-800 transform rotate-45 ${
                adjustedPosition === 'top' ? 'bottom-[-4px] left-1/2 -translate-x-1/2' :
                adjustedPosition === 'bottom' ? 'top-[-4px] left-1/2 -translate-x-1/2' :
                adjustedPosition === 'left' ? 'right-[-4px] top-1/2 -translate-y-1/2' :
                'left-[-4px] top-1/2 -translate-y-1/2'
              }`}
            />
          </div>
        </div>
      )}
    </div>
  );
}